import { useEffect, useState } from 'react';
import { Gift, Share2, Check } from 'lucide-react';
import { loadReferralCode, referralLink, shareReferral } from '../lib/referralShare';

/**
 * Light-theme referral block for page content (rendered on /plan under the
 * pricing cards). Exists because the dark sidebar ReferralCard is invisible
 * on mobile (the sidebar itself is hidden under 900px), so mobile customers
 * had no referral surface at all. Same data source and gating as the sidebar
 * card: give a friend $40 off (matched to WELCOME40, 2026-07-24), get a $40
 * Amazon gift card (reward raised from $25 the same day, F: "give $40, get
 * $40"); renders nothing while loading or for non-payers.
 */


export default function ReferralPanel() {
  const [code, setCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadReferralCode().then((c) => {
      if (!cancelled && c) setCode(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!code) return null;

  const link = referralLink(code);

  // Share sheet on a phone, copy on a computer (lib/referralShare.ts).
  const copyLink = async () => {
    const result = await shareReferral(code, 'referral_panel');
    if (result === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      style={{
        marginTop: 24,
        padding: '22px 24px',
        borderRadius: 24,
        background: '#FFFFFF',
        border: '1px solid rgba(233, 122, 80, 0.35)',
        boxShadow: '0 1px 3px rgba(14, 21, 48, 0.04)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 220, flex: '1 1 260px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: '1rem',
            fontWeight: 700,
            color: '#1A1230',
          }}
        >
          <Gift size={18} style={{ color: '#E97A50', flexShrink: 0 }} />
          Give $40, get $40
        </div>
        <p
          style={{
            margin: '6px 0 0',
            fontSize: '0.85rem',
            lineHeight: 1.5,
            color: 'rgba(26, 18, 48, 0.6)',
          }}
        >
          Know someone getting overcharged? They get $40 off any plan. You get a
          $40 gift card once they've started their case and the 14-day refund
          window has passed.
        </p>
      </div>
      <button
        onClick={copyLink}
        title={link}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '11px 18px',
          borderRadius: 999,
          border: '1px solid rgba(233, 122, 80, 0.45)',
          background: copied ? 'rgba(93, 171, 148, 0.14)' : 'rgba(233, 122, 80, 0.12)',
          color: copied ? '#2F8F4E' : '#C8542A',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 0.15s ease, color 0.15s ease',
        }}
      >
        {copied ? <Check size={15} /> : <Share2 size={15} />}
        {copied ? 'Link copied' : 'Share your link'}
      </button>
    </div>
  );
}
